import React from "react";
import {
  type QueryClient,
  type QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import { useActiveGithubOrgForUser } from "./useConnectedGithubOrgs";
import { useApi } from "@/hooks/useApi";
import * as z from "zod";
import { toast } from "sonner";
import { type NamedOrg, orgApiPath, namedOrgKey } from "@/lib/named-org";

// The backend signals MCP failures in a few shapes:
//   - OAuth discovery/registration failures return `{ error: "OAuth setup
//     failed: ..." }` with a 422. Some of those (known provider missing DCR
//     support) also carry `code` and `provider`; `code` is currently always
//     `"no_dcr"`.
//   - Business-rule guards use Laravel `abort(422, "...")`, which puts the text
//     under `message` (e.g. "Maximum of 100 MCP servers per organization").
//   - Field validation (`$request->validate`) returns `{ message: "The given
//     data was invalid.", errors: { field: ["..."] } }`; the per-field text is
//     more useful than the generic top-level `message`.
const McpApiErrorResponse = z.object({
  response: z.object({
    data: z.object({
      error: z.string().optional(),
      message: z.string().optional(),
      errors: z.record(z.string(), z.array(z.string())).optional(),
      code: z.string().optional(),
      provider: z.string().optional(),
    }),
  }),
});

export interface McpApiError {
  message: string;
  code?: string;
  provider?: string;
}

export function getMcpApiError(error: unknown, fallback: string): McpApiError {
  const parsed = McpApiErrorResponse.safeParse(error);
  const data = parsed.success ? parsed.data.response.data : undefined;
  const firstValidationError = data?.errors
    ? Object.values(data.errors)
        .flat()
        .find((m) => m.length > 0)
    : undefined;
  // Prefer an explicit `error` (OAuth failures), then a specific field
  // validation message, then a top-level `abort()` message, then the fallback.
  return {
    message: data?.error ?? firstValidationError ?? data?.message ?? fallback,
    code: data?.code,
    provider: data?.provider,
  };
}

export function getMcpApiErrorMessage(
  error: unknown,
  fallback: string,
): string {
  return getMcpApiError(error, fallback).message;
}

/** `value` is absent for sensitive vars: the API never returns their values. */
const StdioEnvValue = z.object({
  value: z.string().optional(),
  sensitive: z.boolean(),
});

const McpLastFailure = z.object({
  code: z.string(),
  message: z.string(),
  action: z.string().nullable(),
  // Sanitized failure detail (fixed templates plus a scrubbed stdio stderr
  // tail). Only present for users who can manage the server.
  detail: z.string().nullable().optional(),
  failed_at: z.string().nullable().optional(),
});

const McpLastToolError = z.object({
  code: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
  action: z.string().nullable().optional(),
  detail: z.string().nullable().optional(),
  failed_at: z.string().nullable().optional(),
});

const McpLearnedSummary = z.object({
  tool_count: z.number(),
  updated_at: z.string(),
});

const McpServerUser = z.object({
  id: z.number(),
  name: z.string().nullable().optional(),
  username: z.string().nullable().optional(),
  /** Profile on the org's GitHub instance; the login renders unlinked without it. */
  html_url: z.string().nullable().optional(),
});

/** `automation` servers load only into that automation's runs. */
export const McpServerScope = z.enum(["team", "personal", "automation"]);
export type McpServerScope = z.infer<typeof McpServerScope>;

const McpServer = z.object({
  transport: z.enum(["http", "stdio", "builtin"]).optional().default("http"),
  url: z.string().optional().default(""),
  stdio_command: z.string().nullable().optional(),
  stdio_args: z.array(z.string()).optional().default([]),
  stdio_env_keys: z.array(z.string()).optional().default([]),
  stdio_env: z.record(z.string(), StdioEnvValue).optional().default({}),
  enabled: z.boolean().optional().default(true),
  disabled_tools: z.array(z.string()).optional(),
  display_name: z.string().optional(),
  provider_id: z.string().nullable().optional(),
  oauth_docs_url: z.string().nullable().optional(),
  header_docs_url: z.string().nullable().optional(),
  stdio_docs_url: z.string().nullable().optional(),
  scope: McpServerScope,
  auth_type: z.string().optional().default("header"),
  auth_status: z.string().optional().default("pending"),
  // OAuth account identity (OIDC claims) captured when the connection was
  // authorized; disambiguates whose provider account the tokens belong to.
  oauth_account: z
    .object({
      email: z.string().nullable().optional(),
      name: z.string().nullable().optional(),
      username: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  auth_header_keys: z.array(z.string()).optional().default([]),
  can_manage: z.boolean().optional().default(false),
  created_by: McpServerUser.nullable().optional(),
  last_updated_by: McpServerUser.nullable().optional(),
  created_at: z.string().nullable().optional(),
  last_updated_at: z.string().nullable().optional(),
  last_failure: McpLastFailure.nullable().optional(),
  last_tool_error: McpLastToolError.nullable().optional(),
  learned: McpLearnedSummary.optional(),
  /** Integration flow a pending built-in server needs before its tools act. */
  builtin_connect: z
    .enum(["install_workspace", "link_account"])
    .nullable()
    .optional(),
});

// Same PHP quirk at the top level: empty associative array → []
const McpServersMap = z.preprocess(
  (val) => (Array.isArray(val) && val.length === 0 ? {} : val),
  z.record(z.string(), McpServer),
);

const McpServersResponse = z.object({
  mcp_servers: McpServersMap,
});

export type McpServer = z.infer<typeof McpServer>;
export type McpServerUser = z.infer<typeof McpServerUser>;
export type McpServersResponse = z.infer<typeof McpServersResponse>;

const UpsertMcpServerResponse = McpServersResponse.extend({
  stdio_probe_queued: z.boolean().default(false), // absent until the backend that sends it deploys
});

const McpKnowledgeResponse = z.object({
  tools: z.array(
    z.object({
      tool_name: z.string(),
      input_schema_hash: z.string(),
      updated_at: z.string(),
      fragments: z.array(
        z.object({
          fragment_hash: z.string(),
          fragment: z.record(z.string(), z.unknown()),
        }),
      ),
    }),
  ),
  truncated: z.boolean(),
});

export type McpKnowledgeResponse = z.infer<typeof McpKnowledgeResponse>;

export interface UpsertMcpServerInput {
  name?: string;
  display_name: string;
  url: string;
  transport?: "http" | "stdio" | "builtin";
  stdio_command?: string | null;
  stdio_args?: string[];
  stdio_env?: Record<string, { value: string; sensitive: boolean }>;
  scope: McpServerScope;
  provider_id?: string | null;
  enabled?: boolean;
  disabled_tools?: string[];
  auth_type?: "header" | "oauth" | "none";
  auth_headers?: Record<string, string>;
}

/**
 * Org-aware variant that does NOT call `useActiveGithubOrgForUser`. Use this
 * from components that already have the active org from another source (e.g.
 * the URL pathname) and need that same org to drive the MCP server fetch.
 * Shares the cache key with `useMcpServers` so the two stay in sync once the
 * URL org and active org agree. Mirrors the
 * `useCodesmithSettingsForOrg`/`useCodesmithSettings` pattern.
 */
export function useMcpServersForOrg(org: NamedOrg | null | undefined) {
  const api = useApi();

  return useQuery(
    ["mcp-servers", org],
    async () => {
      const response = await api.get(
        `${orgApiPath(org)}/codesmith-settings/mcp-servers`,
      );
      return McpServersResponse.parse(response.data);
    },
    {
      enabled: !!org,
      // OAuth flows finish in another window; refetching on focus lets any
      // surface showing statuses self-heal even when the callback page's
      // postMessage never reaches us (e.g. a provider's COOP severed
      // window.opener).
      refetchOnWindowFocus: true,
    },
  );
}

// Origin of the backend API — the MCP OAuth callback HTML is served from it.
const API_ORIGIN = process.env.NEXT_PUBLIC_API_URL
  ? new URL(process.env.NEXT_PUBLIC_API_URL).origin
  : "";

const McpOAuthResultMessage = z.object({
  type: z.literal("mcp-oauth-result"),
});

/**
 * Refreshes every cached MCP server list when an MCP OAuth callback page
 * reports its result via postMessage. Mounted once at the app root so the
 * pending → connected transition lands even after the surface that opened
 * the popup unmounted or its bounded polling window already closed.
 */
export function useMcpOAuthResultListener(): void {
  const queryClient = useQueryClient();

  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== API_ORIGIN) return;
      if (!McpOAuthResultMessage.safeParse(event.data).success) return;
      void queryClient.invalidateQueries(["mcp-servers"]);
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [queryClient]);
}

export function useMcpServers() {
  const { activeNamedOrg } = useActiveGithubOrgForUser();
  return useMcpServersForOrg(activeNamedOrg);
}

export function useMcpKnowledge(
  org: NamedOrg | null | undefined,
  serverName: string | null,
  enabled: boolean,
) {
  const api = useApi();

  return useQuery(
    ["mcp-knowledge", org, serverName],
    async () => {
      const response = await api.get(
        `${orgApiPath(org)}/codesmith-settings/mcp-servers/${encodeURIComponent(
          serverName ?? "",
        )}/knowledge`,
      );
      return McpKnowledgeResponse.parse(response.data);
    },
    {
      enabled: !!org && !!serverName && enabled,
    },
  );
}

/**
 * Refreshes every cached MCP server list when a turn finishes.
 *
 * Registration and tool-call failures are recorded while the agent works,
 * and nothing else refetches while the session view stays mounted — so
 * without this the chat surfaces keep rendering the status from page load
 * (typically "Connected") while the settings page, which refetches on
 * mount, shows the failure and its Re-authorize CTA. Invalidating the whole
 * prefix covers the session's org and the dashboard's active org, which the
 * chat surfaces resolve independently.
 */
export function useRefreshMcpServersAfterTurn(isTurnActive: boolean): void {
  const queryClient = useQueryClient();
  const wasTurnActive = React.useRef(isTurnActive);

  React.useEffect(() => {
    const justFinished = wasTurnActive.current && !isTurnActive;
    wasTurnActive.current = isTurnActive;
    if (!justFinished) return;
    void queryClient.invalidateQueries(["mcp-servers"]);
  }, [isTurnActive, queryClient]);
}

/**
 * Org-aware variant that does NOT call `useActiveGithubOrgForUser`. Use this
 * from components that resolve their org from another source (e.g. the
 * session's installation) so the save hits that org's MCP config. Shares
 * the cache key with `useMcpServersForOrg` for the same org.
 */
export function useUpsertMcpServerForOrg(org: NamedOrg | null | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    async (input: UpsertMcpServerInput) => {
      const response = await api.put(
        `${orgApiPath(org)}/codesmith-settings/mcp-servers`,
        input,
      );
      return UpsertMcpServerResponse.parse(response.data);
    },
    {
      onError: (error) => {
        toast.error(getMcpApiErrorMessage(error, "Failed to save MCP server"));
      },
      onSuccess: ({ mcp_servers, stdio_probe_queued }, input) => {
        queryClient.setQueryData(["mcp-servers", org], { mcp_servers });
        if (stdio_probe_queued && input.name) {
          refetchUntilQueuedProbeSettles(
            queryClient,
            ["mcp-servers", org],
            input.name,
          );
        }
      },
    },
  );
}

// ProbeSavedStdioMcpServerJob can take up to its 200s job timeout to flip the row.
const QUEUED_PROBE_REFETCH_DELAYS_MS = [
  5_000, 15_000, 30_000, 60_000, 120_000, 210_000,
];

function refetchUntilQueuedProbeSettles(
  queryClient: QueryClient,
  queryKey: QueryKey,
  serverName: string,
): void {
  for (const delay of QUEUED_PROBE_REFETCH_DELAYS_MS) {
    setTimeout(() => {
      const cached = McpServersResponse.safeParse(
        queryClient.getQueryData(queryKey),
      );
      if (
        !cached.success ||
        cached.data.mcp_servers[serverName]?.auth_status !== "error"
      ) {
        return;
      }
      void queryClient.invalidateQueries(queryKey);
    }, delay);
  }
}

export function useUpsertMcpServer() {
  const { activeNamedOrg } = useActiveGithubOrgForUser();
  return useUpsertMcpServerForOrg(activeNamedOrg);
}

// Nested under the org's key, so org-wide invalidations (OAuth callback, turn end, stdio probe) refresh it too.
export function automationMcpServersKey(
  org: NamedOrg | null | undefined,
  automationId: string | undefined,
) {
  return ["mcp-servers", org, "automation", automationId];
}

/** Automation hooks idle until the automation resolves, as org hooks do until the org does (`orgApiPath`). */
function resolvedAutomationId(automationId: string | undefined): string {
  if (!automationId) {
    throw new Error("Automation is not resolved");
  }
  return automationId;
}

/** The backend answers every MCP mutation with this list when the request names the automation. */
function automationMcpServersPath(
  org: NamedOrg | null | undefined,
  automationId: string | undefined,
  suffix = "",
) {
  const query = new URLSearchParams({
    automation_id: resolvedAutomationId(automationId),
  });
  return `${orgApiPath(org)}/codesmith-settings/mcp-servers${suffix}?${query}`;
}

export function useAutomationMcpServers(
  org: NamedOrg | null | undefined,
  automationId: string | undefined,
) {
  const api = useApi();

  return useQuery(
    automationMcpServersKey(org, automationId),
    async () => {
      const response = await api.get(
        automationMcpServersPath(org, automationId),
      );
      return McpServersResponse.parse(response.data);
    },
    { enabled: !!org && !!automationId, refetchOnWindowFocus: true },
  );
}

/**
 * The servers a session's tool calls can reach: the org's team and personal
 * servers, plus an automation run's own servers. Server names are unique per
 * installation, so the two lists merge by key.
 */
export function useSessionMcpServers(
  org: NamedOrg | null | undefined,
  automationId: string | undefined,
) {
  const orgServers = useMcpServersForOrg(org);
  const automationServers = useAutomationMcpServers(org, automationId);
  const { refetch: refetchOrgServers } = orgServers;
  const { refetch: refetchAutomationServers } = automationServers;

  const data = React.useMemo(
    () => mergeMcpServerLists(orgServers.data, automationServers.data),
    [orgServers.data, automationServers.data],
  );

  const refetch = React.useCallback(async () => {
    // react-query's refetch ignores `enabled`, so a non-automation session skips the automation list itself.
    const [orgResult, automationResult] = await Promise.all([
      refetchOrgServers(),
      automationId ? refetchAutomationServers() : undefined,
    ]);
    return {
      data: mergeMcpServerLists(orgResult.data, automationResult?.data),
      dataUpdatedAt: mergedDataUpdatedAt([orgResult, automationResult]),
    };
  }, [automationId, refetchOrgServers, refetchAutomationServers]);

  return {
    data,
    dataUpdatedAt: mergedDataUpdatedAt([orgServers, automationServers]),
    refetch,
  };
}

function mergeMcpServerLists(
  orgServers: McpServersResponse | undefined,
  automationServers: McpServersResponse | undefined,
): McpServersResponse | undefined {
  if (!orgServers && !automationServers) return undefined;
  return {
    mcp_servers: {
      ...orgServers?.mcp_servers,
      ...automationServers?.mcp_servers,
    },
  };
}

/**
 * A merged list is as fresh as its older loaded half, so OAuth completion
 * never reads a status the other list hasn't refetched yet. A list that never
 * loaded (e.g. a 404 for an automation the viewer can't see) doesn't count.
 */
function mergedDataUpdatedAt(
  lists: Array<
    { data?: McpServersResponse; dataUpdatedAt: number } | undefined
  >,
): number {
  const loaded = lists.flatMap((list) => (list?.data ? [list] : []));
  if (loaded.length === 0) return 0;
  return Math.min(...loaded.map((list) => list.dataUpdatedAt));
}

export function useUpsertAutomationMcpServer(
  org: NamedOrg | null | undefined,
  automationId: string | undefined,
) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    async (input: UpsertMcpServerInput) => {
      const response = await api.put(
        `${orgApiPath(org)}/codesmith-settings/mcp-servers`,
        {
          ...input,
          scope: "automation",
          automation_id: resolvedAutomationId(automationId),
        },
      );
      return UpsertMcpServerResponse.parse(response.data);
    },
    {
      onError: (error) => {
        toast.error(getMcpApiErrorMessage(error, "Failed to save MCP server"));
      },
      onSuccess: ({ mcp_servers, stdio_probe_queued }, input) => {
        const queryKey = automationMcpServersKey(org, automationId);
        queryClient.setQueryData(queryKey, { mcp_servers });
        if (stdio_probe_queued && input.name) {
          refetchUntilQueuedProbeSettles(queryClient, queryKey, input.name);
        }
      },
    },
  );
}

export function useDeleteAutomationMcpServer(
  org: NamedOrg | null | undefined,
  automationId: string,
) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    async (serverName: string) => {
      const response = await api.delete(
        automationMcpServersPath(
          org,
          automationId,
          `/${encodeURIComponent(serverName)}`,
        ),
      );
      return McpServersResponse.parse(response.data);
    },
    {
      onError: () => {
        toast.error("Failed to delete MCP server");
      },
      onSuccess: (data) => {
        queryClient.setQueryData(
          automationMcpServersKey(org, automationId),
          data,
        );
        toast.success("MCP server deleted");
      },
    },
  );
}

export function useDeleteMcpServer() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { activeNamedOrg } = useActiveGithubOrgForUser();

  return useMutation(
    async (serverName: string) => {
      const response = await api.delete(
        `${orgApiPath(activeNamedOrg)}/codesmith-settings/mcp-servers/${serverName}`,
      );
      return McpServersResponse.parse(response.data);
    },
    {
      onError: () => {
        toast.error("Failed to delete MCP server");
      },
      onSuccess: (data) => {
        queryClient.setQueryData(["mcp-servers", activeNamedOrg], data);
        toast.success("MCP server deleted");
      },
    },
  );
}

export interface ToggleMcpServerInput {
  serverName: string;
  /** Desired state for the server, i.e. the state the switch was moved to. */
  enabled: boolean;
}

type ToggleQueueEntry = {
  serverName: string;
  inFlight: boolean;
  hasPendingIntent: boolean;
  pendingEnabled: boolean;
  latestIntentId: number;
  initialRollbackEnabled?: boolean;
  persistedEnabled?: boolean;
};

const TOGGLE_MCP_SERVER_MUTATION_KEY = ["toggle-mcp-server"];

/**
 * Org-aware variant that does NOT call `useActiveGithubOrgForUser`. Use this
 * from components that resolve their org from another source (e.g. the
 * session's installation) so the toggle hits that org's MCP config. Shares
 * the cache key with `useMcpServersForOrg` for the same org.
 */
export function useToggleMcpServerForOrg(org: NamedOrg | null | undefined) {
  return useOptimisticMcpToggle(
    ["mcp-servers", org],
    namedOrgKey(org),
    (serverName) =>
      `${orgApiPath(org)}/codesmith-settings/mcp-servers/${serverName}/toggle`,
  );
}

export function useToggleAutomationMcpServer(
  org: NamedOrg | null | undefined,
  automationId: string | undefined,
) {
  return useOptimisticMcpToggle(
    automationMcpServersKey(org, automationId),
    `${namedOrgKey(org)}:automation:${automationId}`,
    (serverName) =>
      automationMcpServersPath(
        org,
        automationId,
        `/${encodeURIComponent(serverName)}/toggle`,
      ),
  );
}

/** Flips the cached row at once and serializes PATCHes per server, rolling back if the last one fails. */
function useOptimisticMcpToggle(
  queryKey: QueryKey,
  queueKeyPrefix: string | undefined,
  togglePath: (serverName: string) => string,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  const toggleQueuesRef = React.useRef(new Map<string, ToggleQueueEntry>());
  const pendingInvalidationRef = React.useRef(false);

  const setCachedServer = (
    serverName: string,
    updater: (server: McpServer) => McpServer,
  ) => {
    const currentSettings =
      queryClient.getQueryData<McpServersResponse>(queryKey);
    const currentServer = currentSettings?.mcp_servers?.[serverName];

    if (!currentSettings || !currentServer) {
      return undefined;
    }

    queryClient.setQueryData<McpServersResponse>(queryKey, {
      ...currentSettings,
      mcp_servers: {
        ...currentSettings.mcp_servers,
        [serverName]: updater(currentServer),
      },
    });

    return currentServer;
  };

  const mutation = useMutation(
    async ({ serverName, enabled }: ToggleMcpServerInput) => {
      const response = await api.patch(
        togglePath(serverName),
        // Send the target state so the request is idempotent; the backend
        // sets it directly instead of blindly flipping the stored value.
        { enabled },
      );
      return McpServersResponse.parse(response.data);
    },
    {
      mutationKey: TOGGLE_MCP_SERVER_MUTATION_KEY,
    },
  );

  const mergeServerResponse = (
    serverName: string,
    response: McpServersResponse,
  ) => {
    const server = response.mcp_servers[serverName];
    if (!server) return;

    const merged = setCachedServer(serverName, () => server);
    if (!merged) {
      queryClient.setQueryData(queryKey, response);
    }
  };

  const invalidateWhenQueuesAreIdle = () => {
    if (toggleQueuesRef.current.size > 0) {
      pendingInvalidationRef.current = true;
      return;
    }

    pendingInvalidationRef.current = false;
    void queryClient.invalidateQueries(queryKey);
  };

  const processQueue = async (queueKey: string) => {
    const entry = toggleQueuesRef.current.get(queueKey);
    if (!entry || entry.inFlight) return;

    entry.inFlight = true;

    while (entry.hasPendingIntent) {
      const enabled = entry.pendingEnabled;
      const intentId = entry.latestIntentId;
      entry.hasPendingIntent = false;

      try {
        const data = await mutation.mutateAsync({
          serverName: entry.serverName,
          enabled,
        });
        entry.persistedEnabled = data.mcp_servers[entry.serverName]?.enabled;

        if (!entry.hasPendingIntent && entry.latestIntentId === intentId) {
          mergeServerResponse(entry.serverName, data);
        }
      } catch {
        if (!entry.hasPendingIntent && entry.latestIntentId === intentId) {
          const rollbackEnabled =
            entry.persistedEnabled ?? entry.initialRollbackEnabled;

          if (rollbackEnabled !== undefined) {
            setCachedServer(entry.serverName, (server) => ({
              ...server,
              enabled: rollbackEnabled,
            }));
          }

          pendingInvalidationRef.current = true;
          toast.error("Failed to toggle MCP server");
        }
      }
    }

    entry.inFlight = false;
    if (!entry.hasPendingIntent) {
      toggleQueuesRef.current.delete(queueKey);
      if (pendingInvalidationRef.current) {
        invalidateWhenQueuesAreIdle();
      }
    } else {
      void processQueue(queueKey);
    }
  };

  const enqueueToggle = async ({
    serverName,
    enabled,
  }: ToggleMcpServerInput) => {
    await queryClient.cancelQueries(queryKey);

    const queueKey = `${queueKeyPrefix ?? ""}:${serverName}`;
    let entry = toggleQueuesRef.current.get(queueKey);
    const existingServer = setCachedServer(serverName, (server) => ({
      ...server,
      enabled,
    }));

    if (!entry) {
      entry = {
        serverName,
        inFlight: false,
        hasPendingIntent: false,
        pendingEnabled: enabled,
        latestIntentId: 0,
        initialRollbackEnabled: existingServer?.enabled,
      };
      toggleQueuesRef.current.set(queueKey, entry);
    }

    entry.latestIntentId += 1;
    entry.pendingEnabled = enabled;
    entry.hasPendingIntent = true;

    void processQueue(queueKey);
  };

  return {
    ...mutation,
    mutate: (input: ToggleMcpServerInput) => {
      void enqueueToggle(input);
    },
    mutateAsync: enqueueToggle,
  };
}

export function useToggleMcpServer() {
  const { activeNamedOrg } = useActiveGithubOrgForUser();
  return useToggleMcpServerForOrg(activeNamedOrg);
}

const AuthorizeMcpServerResponse = z.object({
  authorize_url: z.string(),
});

/**
 * Org-aware variant that does NOT call `useActiveGithubOrgForUser`. Use this
 * from components that resolve their org from another source (e.g. the
 * session's installation) so the OAuth flow targets that org's server.
 */
export function useAuthorizeMcpServerForOrg(org: NamedOrg | null | undefined) {
  const api = useApi();

  return useMutation(
    async (input: {
      serverName: string;
      popup: Window | null;
      /**
       * Session the re-authorize was launched from (the session-thread
       * re-auth card). The backend enqueues a continuation prompt into it
       * once the OAuth flow lands connected, so the agent resumes without
       * the user typing anything.
       */
      sessionId?: string;
      /**
       * Callers that let the user cancel or restart an OAuth attempt while
       * the authorize request is in flight pass this to report whether the
       * attempt was abandoned; when it returns true the post-success
       * navigation and the failure toast are skipped so a late settlement
       * can't reopen an OAuth tab (or surface an error) for an attempt the
       * user already gave up on.
       */
      isAbandoned?: () => boolean;
    }) => {
      const response = await api.post(
        `${orgApiPath(org)}/codesmith-settings/mcp-servers/${input.serverName}/authorize`,
        input.sessionId ? { assistant_session_id: input.sessionId } : undefined,
      );
      const data = AuthorizeMcpServerResponse.parse(response.data);
      if (!isSafeAuthorizeUrl(data.authorize_url)) {
        throw new Error("Refusing to open a non-https authorization URL");
      }
      return { ...data, popup: input.popup };
    },
    {
      onError: (error, variables) => {
        // Close the blank popup if the API call failed
        variables.popup?.close();
        // An abandoned attempt's card has already moved on (canceled or
        // restarted), so surfacing its failure would be noise.
        if (variables.isAbandoned?.()) return;
        toast.error(
          getMcpApiErrorMessage(error, "Failed to start OAuth authorization"),
        );
      },
      onSuccess: (data, variables) => {
        if (variables.isAbandoned?.()) return;
        if (data.popup && !data.popup.closed) {
          data.popup.location.href = data.authorize_url;
        } else {
          // Popup was blocked or closed.
          window.open(data.authorize_url, "_blank", "popup");
        }
      },
    },
  );
}

/**
 * The authorize URL comes from the MCP server's OAuth metadata and is loaded
 * into a same-origin popup, so a `javascript:` URL would run on this origin.
 * Plain http is accepted only for loopback, e.g. a local mock OAuth server.
 */
function isSafeAuthorizeUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  if (parsed.protocol === "https:") return true;

  const loopbackHosts = ["localhost", "127.0.0.1", "[::1]"];
  return parsed.protocol === "http:" && loopbackHosts.includes(parsed.hostname);
}

export function useAuthorizeMcpServer() {
  const { activeNamedOrg } = useActiveGithubOrgForUser();
  return useAuthorizeMcpServerForOrg(activeNamedOrg);
}

const McpTool = z.object({
  name: z.string(),
  description: z.string().nullable(),
  // Raw MCP inputSchema for the client-side draft 2020-12 compatibility
  // check; null when missing or too large for the handshake payload.
  input_schema: z.unknown().nullable().optional(),
});

export type McpTool = z.infer<typeof McpTool>;

// Result from POST /codesmith-settings/mcp-servers/test. The endpoint always
// returns 200 with this shape; check `ok` for handshake success. `tools` is
// null when the server's tools couldn't be enumerated (null means "unknown",
// not "no tools").
const McpTestResult = z.object({
  ok: z.boolean(),
  http_status: z.number().nullable(),
  server_info: z
    .object({
      name: z.string().nullable(),
      version: z.string().nullable(),
      protocol_version: z.string().nullable(),
    })
    .nullable(),
  tools: z.array(McpTool).nullable().optional(),
  error: z.string().nullable(),
  /** False when a concurrent status write kept the probe from advancing the saved row. */
  restarted: z.boolean().nullable().optional(),
});

export type McpTestResult = z.infer<typeof McpTestResult>;

export type TestMcpServerInput =
  | {
      transport?: "http";
      url: string;
      auth_type: "header" | "oauth";
      auth_headers?: Record<string, string>;
      // When provided, the backend uses the saved server's stored credentials
      // (lets users re-test an existing server with its real auth).
      name?: string;
    }
  | {
      transport: "stdio";
      stdio_command: string;
      stdio_args: string[];
      // Names the key set to test: non-blank values override, blank values
      // fall back to the saved server's stored env (when `name` matches a
      // saved stdio server with the same command line), and stored keys
      // absent from the map are excluded.
      stdio_env?: Record<string, string>;
      name?: string;
    };

/**
 * Org-aware variant that does NOT call `useActiveGithubOrgForUser`; see
 * `useUpsertMcpServerForOrg` for when to use it.
 */
export function useTestMcpServerForOrg(org: NamedOrg | null | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    async (input: TestMcpServerInput) => {
      const response = await api.post(
        `${orgApiPath(org)}/codesmith-settings/mcp-servers/test`,
        input,
      );
      return McpTestResult.parse(response.data);
    },
    {
      onSuccess: (data, input) => {
        // Successful named stdio probes clear ERROR on the saved row; refresh
        // the list so Retry / dialog Test drop the failure CTA without a reload.
        if (data.ok && input.transport === "stdio" && input.name) {
          void queryClient.invalidateQueries(["mcp-servers", org]);
        }
      },
    },
  );
}

export function useTestMcpServer() {
  const { activeNamedOrg } = useActiveGithubOrgForUser();
  return useTestMcpServerForOrg(activeNamedOrg);
}

export function useDisconnectMcpServer() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { activeNamedOrg } = useActiveGithubOrgForUser();

  return useMutation(
    async (serverName: string) => {
      const response = await api.post(
        `${orgApiPath(activeNamedOrg)}/codesmith-settings/mcp-servers/${serverName}/disconnect`,
      );
      return McpServersResponse.parse(response.data);
    },
    {
      onError: () => {
        toast.error("Failed to disconnect MCP server");
      },
      onSuccess: (data) => {
        queryClient.setQueryData(["mcp-servers", activeNamedOrg], data);
        toast.success("MCP server disconnected");
      },
    },
  );
}
